import {HTMLAttributes} from 'react';
import {UrlLink} from '../../../applink/UrlLink';
import {twMerge} from '../../../common/twMerge';
import {TAB_TYPE_QUERY_KEY} from '../../../domain/content/[seriesId]/constant';
import {ContentHomeTabType} from '../../../domain/content/[seriesId]/type';
import {getCategoryIcon} from '../../../domain/content/[seriesId]/util/getCategoryIcon';
import {getOverviewPublishPeriodText} from '../../../domain/content/[seriesId]/util/getOverviewPublishPeriodText';
import {CategoryType, Maybe, OnIssue} from '../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {Icon} from '../../atoms/Icon/Icon';
import {IconType} from '../../atoms/Icon/IconType.enum';
import {SeparatorType} from '../../atoms/Separator/SeparatorType.enum';
import {Text} from '../../atoms/Text/Text';
import {TextsWithSeparator} from '../../molecules/TextsWithSeparator/TextsWithSeparator';

export interface ContentOverviewInfoProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
  title?: string;
  author?: Maybe<string>;
  pageViewCountText: string;
  publishPeriod?: Maybe<string>;
  onIssue?: Maybe<OnIssue>;
  ratingText?: string;
  categoryType?: CategoryType;
  categoryDisplayName: string;
  subCategory?: string;
  isMobile: boolean;
  isLikeOn?: Maybe<boolean>;
  onClickLike?: () => void;
  isAlarmOn?: Maybe<boolean>;
  onClickAlarm?: () => void;
  showLikeAndAlarmArea: boolean;
  isCashTypeHome?: boolean;
  onClickCashTypeMore?: () => void;
  isLogin?: boolean;
}

export function ContentOverviewInfo({
  seriesId,
  title,
  author,
  pageViewCountText,
  publishPeriod,
  onIssue,
  ratingText,
  categoryType,
  categoryDisplayName,
  subCategory,
  isMobile,
  isLikeOn,
  isAlarmOn,
  showLikeAndAlarmArea,
  onClickAlarm,
  onClickLike,
  isCashTypeHome,
  onClickCashTypeMore,
  isLogin,
  className,
  ...props
}: ContentOverviewInfoProps) {
  return (
    <div
      className={twMerge(
        'relative px-18pxr text-center',
        !isMobile && 'bg-theme-bg-surface-alt',
        className
      )}
      {...props}
    >
      <UrlLink
        url={
          isMobile
            ? `/content/${seriesId}?${TAB_TYPE_QUERY_KEY}=${ContentHomeTabType.about}`
            : undefined
        }
        style={{
          // 정보탭 링크 클릭시 예외적으로 하이라이트를 없애줍니다. (KW-8201)
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <div
          className="flex flex-col items-center"
          onClick={e =>
            isMobile ? LOG_CLICK_EVENT('정보영역_클릭', e) : undefined
          }
          {...putTiaraObjectProps({
            click: {layer1: '정보'},
          })}
        >
          <Text
            className={twMerge(
              'font-large3-bold mb-3pxr text-ellipsis break-all text-theme-solid-100',
              isMobile ? 'line-clamp-1' : 'line-clamp-2'
            )}
            style={{
              overflowWrap: 'anywhere',
            }}
          >
            {title}
          </Text>
          <Text
            className={twMerge(
              'font-small2 mb-6pxr text-ellipsis text-theme-solid-100 opacity-70 break-word-anywhere',
              isMobile ? 'line-clamp-1' : 'line-clamp-2'
            )}
          >
            {author}
          </Text>
          <div className="flex h-16pxr items-center justify-center all-child:font-small2 [&>*:not(:last-child)]:mr-10pxr">
            <div className="flex items-center">
              <Icon
                type={getCategoryIcon(categoryType)}
                className="mr-2pxr"
                disableActiveEffect
              />
              <TextsWithSeparator
                separatorType={SeparatorType.ContentHomeDot}
                useStaticDot={false}
                texts={[
                  ...(subCategory && categoryDisplayName !== subCategory
                    ? [categoryDisplayName, subCategory]
                    : [categoryDisplayName]),
                  ...(onIssue && isMobile
                    ? [getOverviewPublishPeriodText(onIssue, publishPeriod)]
                    : []),
                ]}
                className="text-theme-solid-100 opacity-70"
              />
            </div>
            <div className="flex items-center">
              <Icon
                type={IconType.ContentHomeView}
                className="mr-2pxr"
                disableActiveEffect
              />
              <Text className="text-theme-solid-100 opacity-70">
                {pageViewCountText}
              </Text>
            </div>
            {ratingText && (
              <div className="flex items-center">
                <Icon
                  type={IconType.ContentHomeStar}
                  className="mr-2pxr"
                  disableActiveEffect
                />
                <Text className="text-theme-solid-100 opacity-70">
                  {ratingText}
                </Text>
              </div>
            )}
          </div>
          {onIssue && !isMobile && (
            <div className="mt-6pxr flex items-center">
              <Text className="font-small2 text-theme-solid-100 opacity-70">
                {getOverviewPublishPeriodText(onIssue, publishPeriod)}
              </Text>
            </div>
          )}
        </div>
      </UrlLink>

      {!isMobile && (
        <div className="flex justify-center space-x-16pxr">
          {showLikeAndAlarmArea && (
            <div className="mt-24pxr flex justify-center space-x-16pxr pb-24pxr">
              <Icon
                className="cursor-pointer"
                type={
                  isLikeOn
                    ? IconType.NavigationHeartOn
                    : IconType.NavigationHeartOff
                }
                onClick={e => LOG_CLICK_EVENT('좋아요_클릭', e, onClickLike)}
                {...putTiaraObjectProps({
                  click: {
                    copy: `좋아요${isLikeOn ? 'off' : 'on'}`,
                    layer1: '작품개요',
                  },
                })}
              />
              {isLikeOn && (
                <Icon
                  className="cursor-pointer"
                  type={
                    isAlarmOn
                      ? IconType.NavigationAlarmOn
                      : IconType.NavigationAlarmOff
                  }
                  onClick={e => LOG_CLICK_EVENT('알림_클릭', e, onClickAlarm)}
                  {...putTiaraObjectProps({
                    click: {
                      copy: `알림${isAlarmOn ? 'off' : 'on'}`,
                      layer1: '작품개요',
                    },
                  })}
                />
              )}
            </div>
          )}
          {isCashTypeHome && isLogin && (
            <Icon
              className="mt-24pxr pb-24pxr"
              type={IconType.NavigationMore}
              {...putTiaraObjectProps({click: {layer1: '상단바'}})}
              onClick={e =>
                LOG_CLICK_EVENT('더보기_클릭', e, onClickCashTypeMore)
              }
            />
          )}
        </div>
      )}
    </div>
  );
}
